/**
 * Where a media comment points, and the arithmetic every surface reads it through.
 *
 * The API offers two anchors — one frame, or a span — and five places want the same answers
 * out of them: where does it begin, where does it end, and how does it read. Branching on `type`
 * at each of those is how the scrub bar, the filmstrip and the panel end up disagreeing about
 * where a comment points.
 *
 * Seconds from the media start throughout, which is the unit the API takes and the same one
 * cuts, scenes and search ranges use. Deliberately NOT clamped to the media's duration: a
 * comment may be left on a media still being processed, which has no duration yet, and the API
 * does not check anchors against one either.
 *
 * The store, the panel, the player and the timeline all read this module, so it depends on
 * nothing but `units` — a leaf formatter. Keep it that way: a store, page or component import
 * here would put one of those consumers behind the others.
 */

import { formatClockLabel } from '@lib/utils/units';

export type MediaCommentAnchor =
  | { type: 'frame'; timestamp: number }
  | { type: 'range'; startTime: number; endTime: number };

export function anchorStart(anchor: MediaCommentAnchor): number {
  return anchor.type === 'frame' ? anchor.timestamp : anchor.startTime;
}

/** The same instant as {@link anchorStart} for a frame anchor — a point has no width. */
export function anchorEnd(anchor: MediaCommentAnchor): number {
  return anchor.type === 'frame' ? anchor.timestamp : anchor.endTime;
}

/**
 * What a comment written right now would point at: the marked range if there is one, otherwise
 * the frame under the playhead.
 *
 * Adopting the selection rather than offering a separate range-marking gesture is the whole
 * reason the composer needs no controls of its own — the viewer already has a way to mark a
 * span (drag the scrub bar, or pick a scene), and a moment opened from search arrives with its
 * own bounds already marked. A second way to say the same thing would be a second thing to keep
 * in step with `MIN_SELECTION_PERCENT`, the filmstrip's trim head and the Scenes panel.
 *
 * `selection` is `VideoPlayerStore.moment` — null when nothing is marked. A zero-width or
 * inverted one is treated as nothing marked, because a range anchor the API would reject
 * (`endTime` must be greater than `startTime`) is not an anchor.
 */
export function draftAnchor(
  selection: { start: number; end: number } | null,
  currentTime: number,
): MediaCommentAnchor {
  if (selection && selection.end > selection.start) {
    return { type: 'range', startTime: selection.start, endTime: selection.end };
  }
  return { type: 'frame', timestamp: Math.max(0, currentTime) };
}

/**
 * What the anchor reads as: `1:24` for a frame, `1:24 – 2:03` for a span.
 *
 * Lives here rather than in each of the places that show it (the composer's line, a comment's
 * timestamp button) so a frame and a span cannot end up described differently depending on
 * which one you are looking at.
 *
 * An en dash, not a hyphen, matching `SelectionBar` — the two say the same thing about the
 * same range and appear within a few pixels of each other.
 */
export function formatAnchorLabel(anchor: MediaCommentAnchor): string {
  return anchor.type === 'frame'
    ? formatClockLabel(anchor.timestamp)
    : `${formatClockLabel(anchor.startTime)} – ${formatClockLabel(anchor.endTime)}`;
}

/**
 * Which row under a timeline each span-anchored comment is drawn on, so that overlapping spans
 * sit one above the other instead of on top of each other. `0` is the row nearest the timeline.
 *
 * Greedy interval partitioning: in start order, each span takes the first row whose last span
 * has already ended. A span that ends exactly where the next begins shares its row — the API's
 * end is exclusive, so the two genuinely do not overlap.
 *
 * Frame anchors are not given a row. They are drawn over the spans as a distinct dot instead,
 * which is what keeps one pinned to a span's first frame from vanishing inside that span's bar —
 * the case that prompted this.
 *
 * Capped at `maxLanes`, because the space under a scrub bar is not. Past the cap a span goes on
 * the row that frees up soonest, overlapping there rather than growing the stack into whatever
 * sits below the timeline. Deterministic for the same input in any order, so the scrub bar and
 * the filmstrip put the same comment on the same row.
 */
export function assignSpanLanes(
  comments: readonly { id: string; anchor: MediaCommentAnchor }[],
  maxLanes: number,
): Map<string, number> {
  const spans = comments
    .filter((comment) => comment.anchor.type === 'range')
    .sort(
      (a, b) =>
        anchorStart(a.anchor) - anchorStart(b.anchor) ||
        anchorEnd(b.anchor) - anchorEnd(a.anchor) ||
        a.id.localeCompare(b.id),
    );

  /** Where the last span on each row ends. */
  const rowEnds: number[] = [];
  const laneById = new Map<string, number>();

  for (const span of spans) {
    const start = anchorStart(span.anchor);
    let lane = rowEnds.findIndex((end) => end <= start);
    if (lane === -1) {
      lane = rowEnds.length < maxLanes ? rowEnds.length : rowEnds.indexOf(Math.min(...rowEnds));
    }
    rowEnds[lane] = Math.max(rowEnds[lane] ?? -Infinity, anchorEnd(span.anchor));
    laneById.set(span.id, lane);
  }
  return laneById;
}

/**
 * Rows of comment spans, under the scrub bar and along the filmstrip alike — one count, so
 * {@link assignSpanLanes} puts a comment on the same row in both. The scrub bar is what sets it:
 * its hitbox leaves about 12px below the bar, and two 5px rows (7px on a touch screen) are what
 * fit there before a third would crowd whatever sits below the player.
 */
export const COMMENT_SPAN_LANES = 2;

/** A group of frame dots' count, capped at `9+` so the mark stays a dot's size rather than a pill. */
export function formatClusterCount(count: number): string {
  return count > 9 ? '9+' : String(count);
}

/**
 * Groups marks that would be drawn on top of one another, so several comments on one frame — or
 * on frames too close to tell apart at this zoom — read as one mark with a count rather than as
 * one dot hiding the rest.
 *
 * `position` and `minGap` are in the caller's units: percent of a scrub bar, pixels of a
 * filmstrip. "Would overlap" is a question about the screen, not about time — two frames a
 * second apart are distinct on a zoomed-in filmstrip and the same dot on an hour-long scrub bar
 * — so the caller converts its mark size into its own units and this stays unit-free.
 *
 * A mark joins a group while it is within `minGap` of the group's FIRST member, not its last.
 * Chaining off the last would let a run of dots each just under the gap apart fold a whole
 * stretch of the bar into one mark; measuring from the first keeps every group narrower than a
 * dot, and — because a group is drawn at its first member — keeps the drawn groups at least a
 * dot apart from each other.
 *
 * Groups come back in position order, members within a group in the order given, so a caller
 * passing comments in timeline order gets each group's first comment as the one it points at.
 */
export function clusterByPosition<T extends { position: number }>(
  items: readonly T[],
  minGap: number,
): T[][] {
  const sorted = [...items].sort((a, b) => a.position - b.position);
  const groups: T[][] = [];
  for (const item of sorted) {
    const current = groups[groups.length - 1];
    if (current && item.position - current[0].position < minGap) {
      current.push(item);
    } else {
      groups.push([item]);
    }
  }
  return groups;
}

/**
 * Timeline order: where each comment points, then which was written first.
 *
 * The API returns comments in creation order, which is the order they were *written* rather
 * than the order they are *read* — scrubbing through a media and finding the notes out of
 * sequence is the same defect as a transcript that does not follow the audio. `createdAt`
 * breaks the tie so two notes on the same frame keep a stable order rather than swapping
 * places on every re-render.
 */
export function compareByAnchor(
  a: { anchor: MediaCommentAnchor; createdAt: number },
  b: { anchor: MediaCommentAnchor; createdAt: number },
): number {
  return anchorStart(a.anchor) - anchorStart(b.anchor) || a.createdAt - b.createdAt;
}
